import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api, ErreurApi, lire, requetes } from '../api'
import {
  abonnementCourant,
  activerNotifications,
  corpsAbonnement,
  empreinteAbonnement,
  permission,
  priseEnCharge,
} from '../pwa/notifications'
import { formaterInstant } from './Historique'
import { Icone } from './Icone'
import { Section, Vide } from './Section'
import { confirmer } from './confirmation'

// Notifications sur les téléphones : le bouton « Activer les notifications sur cet appareil »
// (demandé sur un geste, comme iOS l'exige), l'explication quand l'appareil ne le permet pas, la
// liste des appareils de la personne, qu'elle révoque. Un code SMS vaut dix minutes : sans
// notification, il est presque toujours perdu.

/** L'empreinte de l'abonnement de ce navigateur, null s'il n'est pas abonné. */
function useEmpreinteCourante(): [string | null | undefined, () => void] {
  const [empreinte, setEmpreinte] = useState<string | null | undefined>(undefined)
  const [tour, setTour] = useState(0)
  useEffect(() => {
    let actif = true
    void abonnementCourant()
      .then((a) => (a ? empreinteAbonnement(a) : null))
      .catch(() => null)
      .then((e) => actif && setEmpreinte(e))
    return () => {
      actif = false
    }
  }, [tour])
  return [empreinte, () => setTour((t) => t + 1)]
}

/**
 * État de cet appareil et bouton d'activation. `compact` : dans l'écran d'un défi, rien quand
 * c'est déjà fait.
 */
export function ActiverNotifications({ compact = false }: { compact?: boolean }) {
  const client = useQueryClient()
  const etat = useQuery(requetes.notifications)
  const [empreinte, relire] = useEmpreinteCourante()
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const support = priseEnCharge()
  const connu =
    empreinte !== undefined &&
    empreinte !== null &&
    (etat.data?.appareils ?? []).some((a) => a.empreinte === empreinte)

  // Abonné ici mais inconnu du serveur (révoqué ailleurs, clés renouvelées) : on le redonne,
  // sans rien demander (la permission est déjà accordée).
  useEffect(() => {
    if (!etat.data?.configuree || !empreinte || connu || permission() !== 'granted') return
    void abonnementCourant().then(async (a) => {
      if (!a) return
      await lire(api.notifications.appareils.$post({ json: corpsAbonnement(a) })).catch(() => {})
      await client.invalidateQueries({ queryKey: ['notifications'] })
    })
  }, [etat.data?.configuree, empreinte, connu, client])

  if (!etat.data) return null
  if (!etat.data.configuree)
    return compact ? null : (
      <p className="discret">Notifications non configurées sur ce serveur (Supervision).</p>
    )
  if (support === 'ios_a_installer')
    return (
      <p className="discret">
        Sur iPhone, les notifications ne marchent que dans l’appli installée :{' '}
        <Link to="/installer">ajoutez-la d’abord à l’écran d’accueil</Link>, puis activez-les depuis
        l’appli.
      </p>
    )
  if (support === 'sans_service_worker')
    return compact ? null : (
      <p className="discret">Notifications disponibles dans l’appli déployée seulement.</p>
    )
  if (support === 'non_prise_en_charge')
    return compact ? null : <p className="discret">Ce navigateur ne reçoit pas de notifications.</p>
  if (connu && permission() === 'granted')
    return compact ? null : (
      <p className="discret">
        <Icone nom="check_circle" /> Notifications activées sur cet appareil.
      </p>
    )
  if (permission() === 'denied')
    return (
      <p className="discret">
        Notifications refusées sur cet appareil : les autoriser dans les réglages du téléphone, pour
        cette appli, puis revenir ici.
      </p>
    )
  const clePublique = etat.data.clePublique!
  return (
    <div className="notifications-activer">
      {compact && (
        <p className="discret">Pour être prévenu la prochaine fois, même appli fermée :</p>
      )}
      <button
        type="button"
        className="bouton"
        disabled={occupe}
        onClick={() => {
          setErreur(null)
          setOccupe(true)
          // Pas d'attente avant la demande de permission : le geste doit rester frais.
          void activerNotifications(clePublique)
            .then((corps) => lire(api.notifications.appareils.$post({ json: corps })))
            .then(() => client.invalidateQueries({ queryKey: ['notifications'] }))
            .then(relire)
            .catch((x: unknown) =>
              setErreur(
                x instanceof ErreurApi || x instanceof Error
                  ? x.message
                  : 'Notifications non activées.',
              ),
            )
            .finally(() => setOccupe(false))
        }}
      >
        <Icone nom="notifications" /> Activer les notifications sur cet appareil
      </button>
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}

/** Réglages, onglet de la personne : cet appareil, ses appareils, un essai. */
export function SectionNotifications() {
  const client = useQueryClient()
  const etat = useQuery(requetes.notifications)
  const [empreinte] = useEmpreinteCourante()
  const [message, setMessage] = useState<string | null>(null)
  const appareils = etat.data?.appareils ?? []
  return (
    <Section titre="Notifications">
      <p className="discret">
        Quand un portail demande un code ou une validation, la notification arrive sur chacun de vos
        appareils où elle est activée ; la toucher ouvre l’écran de la vérification. Quand une tâche
        vous est confiée, une seule notification, un quart d’heure plus tard (de 9 h à 20 h),
        regroupe toutes vos tâches nouvelles.
      </p>
      <ActiverNotifications />
      {etat.data?.configuree && appareils.length === 0 && <Vide>Aucun appareil.</Vide>}
      {appareils.map((a) => (
        <div key={a.id} className="ligne">
          <div className="ligne-contenu">
            <Icone nom="smartphone" /> <strong>{a.libelle}</strong>
            {a.empreinte === empreinte && <span className="badge">Cet appareil</span>}
            <div className="discret">
              Ajouté le {formaterInstant(a.creeLe)}
              {a.dernierEnvoiLe &&
                ` · dernière notification le ${formaterInstant(a.dernierEnvoiLe)}`}
              {a.dernierEchecLe &&
                ` · dernier échec le ${formaterInstant(a.dernierEchecLe)} (${a.dernierEchec})`}
            </div>
          </div>
          <div className="ligne-actions">
            <button
              type="button"
              className="bouton-discret"
              onClick={async () => {
                if (!(await confirmer(`Ne plus notifier ${a.libelle} ?`))) return
                await lire(api.notifications.appareils[':id'].$delete({ param: { id: a.id } }))
                if (a.empreinte === empreinte) await (await abonnementCourant())?.unsubscribe()
                await client.invalidateQueries({ queryKey: ['notifications'] })
              }}
            >
              Révoquer
            </button>
          </div>
        </div>
      ))}
      {appareils.length > 0 && (
        <div className="boutons">
          <button
            type="button"
            className="bouton-discret"
            onClick={async () => {
              try {
                const r = await lire(api.notifications.essai.$post())
                setMessage(
                  r.envoyes > 0
                    ? `Essai envoyé à ${r.envoyes} appareil(s).`
                    : 'Aucun appareil n’a reçu l’essai.',
                )
              } catch (x) {
                setMessage(x instanceof ErreurApi ? x.message : 'Essai impossible.')
              } finally {
                await client.invalidateQueries({ queryKey: ['notifications'] })
              }
            }}
          >
            Envoyer un essai
          </button>
        </div>
      )}
      {message && (
        <p className="discret" role="status">
          {message}
        </p>
      )}
    </Section>
  )
}
